import clsx from 'clsx';
import { isEmpty, toString } from 'lodash';
import { MAX_CHAR_LENGTH } from 'common/constants/general';
import {
  fullyDilutedOwnershipFormat,
  largeCurrencyFormat,
  twoDecimalPercentFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import { ValueWithLinkViewer } from 'components';
import {
  FIRM_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
  FIRM_SUMMARY_SPREADSHEET_COLUMNS,
  FIRM_SUMMARY_SPREADSHEET_ROW_SPAN,
  FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS,
  FIRM_SUMMARY_SPREADSHEET_TOOLTIP_CLASSNAME,
  FIRM_SUMMARY_SPREADSHEET_TOTAL_ALIAS,
  getFirmSummaryColumn,
} from 'pages/Firms/common/constants';
import { FundSelector } from 'pages/Firms/components';
import { FirmSummaryCell } from 'pages/Firms/hooks/useFirmSummarySheet';
import { generateTotalExpression, getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CellParserParams, CreateParserParams, CreateParserReturn } from './createParser.types';
import { handleDraftCells, handleParentCells } from './utils';

const {
  fund: FUND,
  valuation_date: VALUATION_DATE,
  initial_investment_date: INITIAL_INVESTMENT_DATE,
  most_recent_investment_date: MOST_RECENT_INVESTMENT_DATE,
  fully_diluted_ownership_percentage: FULLY_DILUTED_OWNERSHIP_PERCENTAGE,
  invested_capital: INVESTED_CAPITAL,
  realized_value: REALIZED_VALUE,
  unrealized_value: UNREALIZED_VALUE,
  total_value: TOTAL_VALUE,
  gross_irr: GROSS_IRR,
  moic: MOIC,
} = FIRM_SUMMARY_SPREADSHEET_COLUMNS;

const cellParser = (params: CellParserParams): FirmSummaryCell => {
  const {
    allNonDraftParentsExpression,
    allParentsExpression,
    alphabet,
    colNumber,
    column,
    columnLegend,
    firmSummaryClassNames,
    fundsAvailable,
    key,
    row,
    rowNumber,
    selectedFund,
    setSelectedFund,
  } = params;

  const { alias: rowAlias, data: rowData } = getObjectValue(row);
  const { value: columnCellValue, ...columnCellData } = getObjectValue(column[toString(rowAlias)]);

  // Creating cell from row and column
  const cell = { ...row, data: { ...rowData, ...columnCellData }, key, value: columnCellValue } as FirmSummaryCell;
  const { fundUrl } = getObjectValue(cell.data);

  const currentColumn = getFirmSummaryColumn(colNumber);
  const { tooltip, title } = getObjectValue(currentColumn);

  const totalValueColumnLegend = alphabet[TOTAL_VALUE.column - 1];
  const investedCapitalColumnLegend = alphabet[INVESTED_CAPITAL.column - 1];

  const totalValueCellKey = `${totalValueColumnLegend}${rowNumber}`;
  const investedCapitalCellKey = `${investedCapitalColumnLegend}${rowNumber}`;

  // MOIC = total value / invested capital
  const MOICExpression = `IF(${investedCapitalCellKey}>0,${totalValueCellKey}/${investedCapitalCellKey},0)`;

  // Generate the cell based on the rowAlias
  switch (rowAlias) {
    // Generate title cells
    case FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS:
      cell.value = title;

      if (tooltip) {
        cell.tooltipMessages?.push(tooltip);
      }

      // Fund selector
      if (colNumber === FUND.column) {
        cell.className = clsx(cell.className, 'text-right');
        cell.forceComponent = true;
        cell.value = '';
        cell.valueViewer = () => (
          <FundSelector handleOnChange={setSelectedFund} options={fundsAvailable} value={selectedFund} />
        );
      }
      break;

    // Generate total cells
    case FIRM_SUMMARY_SPREADSHEET_TOTAL_ALIAS:
      switch (colNumber) {
        case FUND.column:
        case VALUATION_DATE.column:
        case INITIAL_INVESTMENT_DATE.column:
        case MOST_RECENT_INVESTMENT_DATE.column:
          break;

        case INVESTED_CAPITAL.column:
          cell.expr = allParentsExpression;
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          break;

        case REALIZED_VALUE.column:
        case UNREALIZED_VALUE.column:
        case TOTAL_VALUE.column:
          cell.expr = allNonDraftParentsExpression;
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';

          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case GROSS_IRR.column:
          // Gross IRR is not calculated for total
          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case MOIC.column:
          cell.expr = `=${MOICExpression}`;
          cell.format = xStandardSuffixFormat;
          cell.gridType = 'number';

          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        default:
          break;
      }
      break;

    default:
      // Generate column cells
      switch (colNumber) {
        case FUND.column:
          // Class to show tooltip if text is truncated
          cell.className = clsx(cell.className, 'text-right', FIRM_SUMMARY_SPREADSHEET_TOOLTIP_CLASSNAME);
          cell.forceComponent = true;
          cell.valueViewer = () => <ValueWithLinkViewer name={toString(cell.value)} url={fundUrl} />;
          break;

        case VALUATION_DATE.column:
        case INITIAL_INVESTMENT_DATE.column:
        case MOST_RECENT_INVESTMENT_DATE.column:
          break;

        case FULLY_DILUTED_OWNERSHIP_PERCENTAGE.column:
          cell.format = fullyDilutedOwnershipFormat;
          cell.gridType = 'percentage';

          handleParentCells({ cell, colNumber, rowNumber });
          break;

        case INVESTED_CAPITAL.column:
        case REALIZED_VALUE.column:
        case UNREALIZED_VALUE.column:
        case TOTAL_VALUE.column:
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';

          handleParentCells({ cell, colNumber, rowNumber });
          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case GROSS_IRR.column:
          cell.format = twoDecimalPercentFormat({});
          cell.gridType = 'percentage';

          handleParentCells({ cell, colNumber, rowNumber });
          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        case MOIC.column:
          cell.expr = `=${MOICExpression}`;
          cell.format = xStandardSuffixFormat;
          cell.gridType = 'number';

          handleDraftCells({ cell, firmSummaryClassNames });
          break;

        default:
          break;
      }
      break;
  }

  // Default values
  const {
    allowEmptyValues = true,
    allowNegativeValue = FIRM_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
    customKey = null,
    dbType = 'string',
    expr = '',
    format = null,
    gridType = 'string',
    maxNumberChars = MAX_CHAR_LENGTH,
    readOnly = true,
    rowSpan = FIRM_SUMMARY_SPREADSHEET_ROW_SPAN,
    tooltipMessages = [],
    value = '',
  } = getObjectValue(cell);

  return {
    ...cell,
    allowEmptyValues,
    allowNegativeValue,
    columnLegend,
    customKey,
    dbType,
    expr: getExpression({ expr: toString(expr), columnLegend }),
    format,
    gridType,
    key,
    maxNumberChars,
    readOnly,
    rowSpan,
    tooltipMessages,
    value,
  } as FirmSummaryCell;
};

const createParser = (params: CreateParserParams): CreateParserReturn => {
  const {
    columns,
    firmSummaryClassNames,
    fundsAvailable,
    nonDraftParentFundsByCompanyRows,
    parentFundsByCompanyRows,
    rowConfig,
    selectedFund,
    setSelectedFund,
  } = params;

  const cells = {} as CreateParserReturn;

  const alphabet = alphabetGenerator([], columns.length);

  const allParentsTotalExpression = generateTotalExpression({
    items: parentFundsByCompanyRows,
    generateIndex: false,
  });
  const allParentsExpression = !isEmpty(allParentsTotalExpression) ? `=SUM(${allParentsTotalExpression})` : '';

  const allNonDraftParentsTotalExpression = generateTotalExpression({
    items: nonDraftParentFundsByCompanyRows,
    generateIndex: false,
  });
  const allNonDraftParentsExpression = !isEmpty(allNonDraftParentsTotalExpression)
    ? `=SUM(${allNonDraftParentsTotalExpression})`
    : '';

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      const rowNumber = rowIndex + 1;
      const colNumber = colIndex + 1;
      const columnLegend = alphabet[colIndex];

      const key = columnLegend + rowNumber;

      cells[key] = cellParser({
        allNonDraftParentsExpression,
        allParentsExpression,
        alphabet,
        colNumber,
        column,
        columnLegend,
        firmSummaryClassNames,
        fundsAvailable,
        key,
        row,
        rowNumber,
        selectedFund,
        setSelectedFund,
      });
    });
  });

  return cells;
};

export default createParser;
